import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { authApi, User, RegisterFormData } from '../services/api';
import { adminApi, AdminUser } from '../services/adminApi';

interface AuthState {
  user: User | null;
  adminUser: AdminUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdminAuthenticated: boolean;
  isInitialized: boolean; // Track if auth check has completed
}

interface AuthContextType extends AuthState {
  login: (phoneNumber: string) => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  verifyOtp: (phoneNumber: string, otpCode: string) => Promise<void>;
  register: (userData: RegisterFormData) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  checkAdminAuth: () => Promise<void>;
}

type AuthAction = 
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_ADMIN_USER'; payload: AdminUser | null }
  | { type: 'SET_INITIALIZED'; payload: boolean }
  | { type: 'LOGOUT' };

const initialState: AuthState = {
  user: null,
  adminUser: null,
  isLoading: true,
  isAuthenticated: false,
  isAdminAuthenticated: false,
  isInitialized: false,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
        isInitialized: true,
      };
    case 'SET_ADMIN_USER':
      return {
        ...state,
        adminUser: action.payload,
        isAdminAuthenticated: !!action.payload,
        isLoading: false,
        isInitialized: true,
      };
    case 'SET_INITIALIZED':
      return {
        ...state,
        isInitialized: action.payload,
        isLoading: !action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        adminUser: null,
        isAuthenticated: false,
        isAdminAuthenticated: false,
        isLoading: false,
        isInitialized: true,
      };
    default:
      return state;
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export default function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const checkAuth = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Check both admin and user authentication simultaneously
      // This prevents cross-contamination between different browser sessions
      
      const currentPath = window.location.pathname;
      const isAdminPage = currentPath.startsWith('/admin');
      
      // Check admin authentication only when on admin pages
      if (isAdminPage) {
        try {
          const adminResponse = await adminApi.getProfile();
          dispatch({ type: 'SET_ADMIN_USER', payload: adminResponse.data });
        } catch (adminError: any) {
          // Admin auth failed, clear admin user silently
          dispatch({ type: 'SET_ADMIN_USER', payload: null });
          // Only log non-401 errors to avoid console spam for visitors
          if (adminError?.response?.status !== 401 && adminError?.response?.status !== 403) {
            console.error('Admin auth check failed:', adminError);
          }
        }
      } else {
        // Not on admin page, clear admin user state
        dispatch({ type: 'SET_ADMIN_USER', payload: null });
      }
      
      // Always check user authentication (security requirement)
      // On admin pages, 401 is expected if admin is not also a regular user
      try {
        const response = await authApi.getCurrentUser();
        dispatch({ type: 'SET_USER', payload: response.data });
      } catch (error: any) {
        // Clear user silently - this is expected for expired sessions, first-time visitors, or admins who aren't regular users
        dispatch({ type: 'SET_USER', payload: null });
        // Only log unexpected errors, not authentication failures
        // On admin pages, 401 is expected and should be silent
        if (error?.response?.status !== 401 && error?.response?.status !== 403) {
          console.error('User auth check failed:', error);
        }
      }
    } catch (error: any) {
      // Only log unexpected errors
      if (error?.response?.status !== 401 && error?.response?.status !== 403) {
        console.error('Auth check failed:', error);
      }
      dispatch({ type: 'SET_USER', payload: null });
      dispatch({ type: 'SET_ADMIN_USER', payload: null });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
      dispatch({ type: 'SET_INITIALIZED', payload: true });
    }
  };

  const checkAdminAuth = async () => {
    try {
      const adminResponse = await adminApi.getProfile();
      dispatch({ type: 'SET_ADMIN_USER', payload: adminResponse.data });
    } catch (adminError: any) {
      // Admin auth failed, clear admin user silently
      dispatch({ type: 'SET_ADMIN_USER', payload: null });
      // Only log non-401 errors to avoid console spam
      if (adminError?.response?.status !== 401 && adminError?.response?.status !== 403) {
        console.error('Admin auth check failed:', adminError);
      }
      throw adminError; // Re-throw to allow calling code to handle
    }
  };

  useEffect(() => {
    // Check authentication status on app initialization
    checkAuth();
  }, []);

  const login = async (phoneNumber: string) => {
    await authApi.login({ EmailOrPhone: phoneNumber });
  };

  const loginWithPassword = async (email: string, password: string) => {
    const response = await authApi.login({ EmailOrPhone: email, Password: password });
    if (response.data.user) {
      dispatch({ type: 'SET_USER', payload: response.data.user });
    }
  };

  const verifyOtp = async (phoneNumber: string, otpCode: string) => {
    const response = await authApi.verifyOtp({ EmailOrPhone: phoneNumber, Code: otpCode });
    dispatch({ type: 'SET_USER', payload: response.data.user });
  };

  const register = async (userData: RegisterFormData) => {
    // Convert userType string to enum number for backend
    const registrationData = {
      ...userData,
      userType: (userData.userType === 'Business' ? 1 : 0) as 0 | 1 // Individual = 0, Business = 1
    };
    await authApi.register(registrationData);
  };

  const logout = async () => {
    try {
      // Check if we need to logout admin or regular user
      if (state.isAdminAuthenticated) {
        await adminApi.logout();
      } else if (state.isAuthenticated) {
        await authApi.logout();
      }
    } catch (error) {
      // Handle logout error silently, but still update state
      console.warn('Logout API call failed, but clearing local state');
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    loginWithPassword,
    verifyOtp,
    register,
    logout,
    checkAuth,
    checkAdminAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 
